import React, { useState, useCallback } from 'react';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  touchAction: 'none' // Improves touch handling and removes passive listener warnings
};

const defaultCenter = {
  lat: 23.8103, // Dhaka, Bangladesh
  lng: 90.4125
};

interface LocationPickerProps {
  onLocationSelect: (location: {
    lat: number;
    lng: number;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
  initialLocation?: {
    lat: number;
    lng: number;
    address?: string;
  };
  isPrecise?: boolean; // true for customers (precise location), false for caregivers (general area)
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationSelect,
  initialLocation,
  isPrecise = true
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : null
  );
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [searchValue, setSearchValue] = useState(initialLocation?.address || '');

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMarker({ lat, lng });
        
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }

        extractLocationDetails(place, lat, lng);
      }
    }
  };

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarker({ lat, lng });
      
      // Reverse geocode to get address
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          extractLocationDetails(results[0], lat, lng);
          setSearchValue(results[0].formatted_address);
        }
      });
    }
  };

  const extractLocationDetails = (
    place: google.maps.places.PlaceResult | google.maps.GeocoderResult,
    lat: number,
    lng: number
  ) => {
    let address = '';
    let city = '';
    let state = '';
    let zipCode = '';

    if ('formatted_address' in place) {
      address = place.formatted_address || '';
    }

    if (place.address_components) {
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.long_name;
        }
        if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });
    }

    // If no city found, try to extract from formatted address
    if (!city && address) {
      const addressParts = address.split(',');
      if (addressParts.length > 1) {
        city = addressParts[addressParts.length - 2].trim();
      }
    }

    onLocationSelect({
      lat,
      lng,
      address,
      city,
      state,
      zipCode
    });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMarker({ lat, lng });
          
          if (map) {
            map.panTo({ lat, lng });
            map.setZoom(15);
          }

          // Reverse geocode to get address
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              extractLocationDetails(results[0], lat, lng);
              setSearchValue(results[0].formatted_address);
            }
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = 'Unable to get your current location. ';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please enable location permissions in your device settings and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information is unavailable. Please search for your address or click on the map.';
              break;
            case error.TIMEOUT:
              errorMessage += 'Location request timed out. Please try again or search for your address.';
              break;
            default:
              errorMessage += 'Please search for your address or click on the map.';
          }
          
          alert(errorMessage);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      alert('Geolocation is not supported by your browser. Please search for your address or click on the map.');
    }
  };

  return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <Autocomplete
              onLoad={onAutocompleteLoad}
              onPlaceChanged={onPlaceChanged}
              options={{
                componentRestrictions: { country: 'bd' }, // Restrict to Bangladesh
                fields: ['address_components', 'geometry', 'formatted_address']
              }}
            >
              <input
                type="text"
                placeholder={isPrecise ? "Search for your exact address..." : "Search for your service area..."}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </Autocomplete>
          </div>
          <button
            type="button"
          onClick={getCurrentLocation}
          className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Use Current Location
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <div className="flex gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>
            {isPrecise 
              ? "Select your precise home location where caregivers will provide service. You can search for an address, use your current location, or click on the map."
              : "Select your general service area. This will be shown to care seekers when they browse for caregivers."}
          </p>
        </div>
      </div>
      
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={marker || defaultCenter}
        zoom={marker ? 15 : 12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={onMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {marker && <Marker position={marker} />}
      </GoogleMap>

      {marker && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-800">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">
              {isPrecise ? 'Location selected' : 'Service area selected'}
            </span>
          </div>
          <p className="text-sm text-green-700 mt-1 ml-7">
            Coordinates: {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
